import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useTenant } from "../../contexts/TenantContext";
import {
  fetchClasses,
  fetchCertificationsByUserIds,
  fetchCoordinatorStats,
  fetchDashboard,
  fetchInstructorPendingSignOffs,
  fetchInstructorStats,
  fetchInstructorTasks,
  fetchPendingCourseSubmissions,
  fetchTenantUsersByRole,
} from "../../api/backend";
import { fetchAhaInventory, fetchInstructorClasses, fetchMessagingThreads, fetchUserCertifications } from "../../api/apiClient";
import StudentDashboard from "../../components/dashboard/student/StudentDashboard";
import { buildStudentModel } from "../../components/dashboard/student/buildStudentModel";
import InstructorDashboard from "../../components/dashboard/instructor/InstructorDashboard";
import { buildInstructorModel } from "../../components/dashboard/instructor/buildInstructorModel";
import CoordinatorDashboard from "../../components/dashboard/coordinator/CoordinatorDashboard";
import { buildCoordinatorModel } from "../../components/dashboard/coordinator/buildCoordinatorModel";
import AdminDashboard from "../../components/dashboard/admin/AdminDashboard";
import { buildAdminModel } from "../../components/dashboard/admin/buildAdminModel";

const DAY = 86400000;
const EMPTY_STUDENT = { loading: false, dashboard: {}, unread: 0 };
const EMPTY_INSTRUCTOR = { loading: false, classes: [], tasks: [], signoffs: [], stats: {}, certs: [] };
const EMPTY_STAFF = { loading: false, courses: [], submissions: [], instructors: [], expiringCerts: [], instructorCerts: [], coordinatorStats: {}, ahaInventory: null };

// A center "does AHA" when it has the AHA certificate type set up and turned on (Training Center › Certificates).
const isAhaPartner = (tenantConfig) => (tenantConfig?.certificateTypes || []).some((t) => t.type === "AHA" && t.enabled);

/** Loads `loader()` while `enabled`, exposing its result (or `empty` on failure/disabled) plus a loading flag. */
function useLoaded(enabled, loader, empty, deps) {
  const [state, setState] = useState({ ...empty, loading: Boolean(enabled) });
  useEffect(() => {
    if (!enabled) {
      setState(empty);
      return undefined;
    }
    let active = true;
    setState((current) => ({ ...current, loading: true }));
    Promise.resolve().then(loader)
      .then((result) => { if (active) setState({ ...empty, ...result, loading: false }); })
      .catch(() => { if (active) setState(empty); });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ...deps]);
  return state;
}

/** Student: the aggregate dashboard payload plus the unread message count (a messaging failure must not block it). */
function useStudentData(user, enabled) {
  return useLoaded(enabled && Boolean(user?.id), async () => {
    const unread = fetchMessagingThreads()
      .then((data) => Object.values(data?.threads || {}).reduce((sum, t) => sum + (t.unread || 0), 0))
      .catch(() => 0);
    const [dashboard, count] = await Promise.all([fetchDashboard(user.id), unread]);
    return { dashboard: dashboard || {}, unread: count };
  }, EMPTY_STUDENT, [user?.id]);
}

/** Instructor: my classes (with their session schedules), tasks, sign-offs waiting on me, my stats and credentials. */
function useInstructorData(user, enabled) {
  const tenantId = user?.tenantId;
  return useLoaded(enabled && Boolean(user?.id), async () => {
    const safe = (promise, fallback) => promise.catch(() => fallback);
    const [classes, tasks, signoffs, stats, certs] = await Promise.all([
      safe(fetchInstructorClasses(tenantId, user.id), []),
      safe(fetchInstructorTasks(user.id, tenantId), []),
      safe(fetchInstructorPendingSignOffs(user.id), []),
      safe(fetchInstructorStats(user.id), {}),
      safe(fetchUserCertifications(user.id), []),
    ]);
    return { classes: classes || [], tasks: tasks || [], signoffs: Array.isArray(signoffs) ? signoffs : [], stats: stats || {}, certs: certs || [] };
  }, EMPTY_INSTRUCTOR, [user?.id, tenantId]);
}

/** Coordinator / admin: the tenant's classes, submissions, instructors (and their certifications) and stats. */
function useStaffData(user, tenantConfig, enabled) {
  const tenantId = user?.tenantId;
  const ahaPartner = isAhaPartner(tenantConfig);
  return useLoaded(enabled && Boolean(tenantId), async () => {
    const [courses, submissions, instructors, coordinatorStats, ahaInventory] = await Promise.all([
      fetchClasses(tenantId, ""),
      fetchPendingCourseSubmissions(),
      fetchTenantUsersByRole(tenantId, "INSTRUCTOR").catch(() => []),
      fetchCoordinatorStats(tenantId).catch(() => ({})),
      // AHA inventory is read from AHA itself and is optional: a center that is not an AHA partner just sees nothing.
      ahaPartner ? fetchAhaInventory().catch(() => null) : Promise.resolve(null),
    ]);
    const ids = (instructors || []).map((i) => i.id || i.userId).filter(Boolean);
    const certs = ids.length ? await fetchCertificationsByUserIds(ids, tenantId).catch(() => []) : [];
    const startOfToday = new Date(new Date().setHours(0, 0, 0, 0));
    const horizon = new Date(Date.now() + 90 * DAY);
    // Instructor certs only: already-lapsed ones are not "expiring soon".
    const expiringCerts = (certs || []).filter((c) => c.expires && new Date(c.expires) >= startOfToday && new Date(c.expires) <= horizon);
    return { courses: courses || [], submissions: submissions || [], instructors: instructors || [], expiringCerts, instructorCerts: certs || [], coordinatorStats: coordinatorStats || {}, ahaInventory };
  }, EMPTY_STAFF, [tenantId, ahaPartner]);
}

export default function RoleDashboardPage() {
  const { user, effectiveUser, tenantConfig, activeTenantId, isFeatureEnabled } = useTenant();
  const navigate = useNavigate();
  const dashboardUser = effectiveUser || user;
  const roles = dashboardUser?.roles || [];
  const tenantId = activeTenantId || dashboardUser?.tenantId;
  const has = (...names) => names.some((r) => roles.includes(r));

  // Agency admins have no dashboard here - their real one is AgencyManagementHub. Without this they would fall
  // through to the student dashboard and call the personal-enrollment endpoint for a user who is not enrolled.
  const isPureAgencyAdmin = has("AGENCY_ADMIN") && !has("ADMIN", "GLOBAL_ADMIN", "COURSE_COORDINATOR", "INSTRUCTOR");
  const view = has("ADMIN", "GLOBAL_ADMIN") ? "admin" : has("COURSE_COORDINATOR") ? "coordinator" : has("INSTRUCTOR") ? "instructor" : "student";

  const student = useStudentData(dashboardUser, view === "student" && !isPureAgencyAdmin);
  const instructor = useInstructorData(dashboardUser, view === "instructor");
  const staff = useStaffData(dashboardUser, tenantConfig, view === "admin" || view === "coordinator");
  const certTrackEnabled = isFeatureEnabled ? isFeatureEnabled("certificationTrack") : false;
  const common = { user: dashboardUser, tenantConfig, navigate };

  const model = useMemo(() => {
    if (view === "student") return buildStudentModel({ ...common, loading: student.loading, dashboard: student.dashboard, unread: student.unread, showTrack: certTrackEnabled });
    if (view === "instructor") return buildInstructorModel({ ...common, loading: instructor.loading, classes: instructor.classes, tasks: instructor.tasks, signoffs: instructor.signoffs, stats: instructor.stats, certs: instructor.certs });
    const staffInput = { ...common, loading: staff.loading, courses: staff.courses, submissions: staff.submissions, instructors: staff.instructors, expiringCerts: staff.expiringCerts, stats: staff.coordinatorStats };
    return view === "admin" ? buildAdminModel(staffInput) : buildCoordinatorModel(staffInput);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, student, instructor, staff, dashboardUser, tenantConfig, navigate, certTrackEnabled]);

  if (isPureAgencyAdmin) return <Navigate to="/agency" replace />;
  if (view === "student") return <StudentDashboard data={model} />;
  if (view === "instructor") return <InstructorDashboard data={model} tenantId={tenantId} />;
  if (view === "admin") return <AdminDashboard data={model} raw={staff} tenantId={tenantId} isGlobalAdmin={roles.includes("GLOBAL_ADMIN")} />;
  const aha = isAhaPartner(tenantConfig)
    ? { inventory: staff.ahaInventory, refresh: () => fetchAhaInventory(true), onViewIssued: () => navigate("/aha-ecards") }
    : null;
  return <CoordinatorDashboard data={model} raw={staff} tenantId={tenantId} aha={aha} />;
}
